import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { FacilityFormType, FacilityFormTypes } from '../../constants/app.constants';
import { NavigationStateShareService } from '../../services/navigation-state-share.service';
import { FormTypesData, KeyValueData } from '../../domains/common.domain';
import { ComponentFormData, LookupOptionsData } from '../jurisdiction.domain';
import { AllDropdownKeys, UrlSegments } from '../jurisdiction.constants';
import { CommonService } from '../../services/common.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { CommunicationService } from '../../services/communication.service';
import { ErrorService } from '../../services/error/error.service';
import { SiteElectricityInstallationTechnicalDataService } from './siteelectricity-installation-technical-data.service';
import { FacilitiesFormService } from '../facilities-form/facilities-form.service';
import { EnvironmentService } from '../../services/environment.service';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { concatMap, defaultIfEmpty, tap } from 'rxjs/operators';
import {
  GetInstallationApplicationObject,
  InstallationApplicationData,
  InstallationData,
  MeterDataObject,
  PowerFacilityResponse,
  PowerInstallationResponse,
  PowerMeterData,
  PowerMeterResponse,
  UpdatePowerFacilityResponse,
  UpdatePowerMeterResponse,
} from '../facilities-form/facilities-form.domain';
import { FacilitiesHeatingHiddenFields } from '../../interface/config.interface';
import { DestroyableComponent } from 'src/app/common/destroyable.component';

@Component({
  providers: [FacilitiesFormService, SiteElectricityInstallationTechnicalDataService],
  selector: 'app-siteelectricity-installation-technical-data',
  styleUrls: ['./siteelectricity-installation-technical-data.component.scss'],
  templateUrl: './siteelectricity-installation-technical-data.component.html',
})
export class SiteElectricityInstallationTechnicalDataComponent extends DestroyableComponent implements OnInit {
  @Input() isReadOnly: boolean = false;

  private readonly PROCESS_DETAILS: string = 'process-details';

  currentFormType: FormTypesData;
  data: any;
  externalApplicationId: string;
  installationTechnicalDataFG: FormGroup;
  facilityTypeKey: string;
  facilityTypes: LookupOptionsData[];
  formSubscription: Subscription;
  internalPowerSiteConTypeKey: string;
  internalPowerSiteConTypes: LookupOptionsData[];
  externalPowerSiteConTypeKey: string;
  externalPowerSiteConTypes: LookupOptionsData[];
  siteConCurrentTypeKey: string;
  siteConCurrentTypes: LookupOptionsData[];
  siteConPowerSubTypeKey: string;
  siteConPowerSubTypes: LookupOptionsData[];
  selectedFacilityType: string;
  selectedSiteConCurrentValue: string;
  showDeviceAreaText: boolean;
  currentInstallationFlow: string;
  hiddenFields: FacilitiesHeatingHiddenFields;
  allUnits: KeyValueData;
  isLoading: boolean;
  meterData: MeterDataObject[];
  powerInstallationId: string;
  showNewMeterSection: boolean;
  infoBubblesData: {
    [key: string]: boolean;
  };
  showMeterSwitch: boolean;
  installationId: string;
  isDetailsView: boolean;
  sectionIdentifier = SectionIdentifier;
  applicationId: any;

  constructor(
    private formBuilder: FormBuilder,
    private route: Router,
    private stateShare: NavigationStateShareService,
    private commonService: CommonService,
    private localStorageService: LocalStorageService,
    private communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private errorService: ErrorService,
    private facilitiesFormService: FacilitiesFormService,
    private siteElectricityService: SiteElectricityInstallationTechnicalDataService,
    private activatedRoute: ActivatedRoute
  ) {
    super();
  }

  ngOnInit(): void {
    this.applicationId = this.localStorageService.getItem('application_id');
    this.preventLeak(this.activatedRoute.url).subscribe(urlSegments => {
      this.isDetailsView = urlSegments[0].path === this.PROCESS_DETAILS;
    });
    this.currentFormType = FacilityFormType.find(item => item.key === 'power');
    this.showMeterSwitch = this.environmentService.showMeterSwitch;
    this.infoBubblesData = this.environmentService.getInfoBubblesData('facilitiesData') || null;
    this.showNewMeterSection = this.environmentService.showNewMeterSection;
    this.showDeviceAreaText = false;
    this.currentInstallationFlow = this.localStorageService.getItem('installation_flow');
    this.powerInstallationId = this.localStorageService.getItem('power_installation_id');
    this.hiddenFields = this.environmentService.facilityHeatingHiddenFields;
    this.isLoading = false;
    this.data = null;
    this.meterData = [];
    this.prepareDropdownData();

    if (this.isReadOnly) {
      this.parseData();
    } else {
      this.saveInstallationData();
    }
    // isReadOnly may flip to editable later (details view); the write-back must already be wired
    this.handleFormData();
  }

  get baseDataSectionIdentifier(): SectionIdentifier {
    if (this.currentFormType.key === FacilityFormTypes.Power.key) return SectionIdentifier.TechnicalData_BaseData_Power;
    else if (this.currentFormType.key === FacilityFormTypes.Gas.key) return SectionIdentifier.TechnicalData_BaseData_Gas;
    else if (this.currentFormType.key === FacilityFormTypes.Heating.key) return SectionIdentifier.TechnicalData_BaseData_Heating;
    else if (this.currentFormType.key === FacilityFormTypes.Water.key) return SectionIdentifier.TechnicalData_BaseData_Water;
    else return null;
  }

  get powerConnectionSectionIdentifier(): SectionIdentifier {
    if (this.currentFormType.key === FacilityFormTypes.Power.key) return SectionIdentifier.TechnicalData_PowerConnection_Power;
    else if (this.currentFormType.key === FacilityFormTypes.Gas.key) return SectionIdentifier.TechnicalData_PowerConnection_Gas;
    else if (this.currentFormType.key === FacilityFormTypes.Heating.key) return SectionIdentifier.TechnicalData_PowerConnection_Heating;
    else if (this.currentFormType.key === FacilityFormTypes.Water.key) return SectionIdentifier.TechnicalData_PowerConnection_Water;
    else return null;
  }

  prepareForm(): void {
    const selectedFacility = this.facilityTypes.filter(item => item.value === 'construction_site')[0] || null;
    if (!selectedFacility) {
      this.facilityTypes.push({
        label: 'facilities-data.construction-site',
        value: 'construction_site',
        order: this.facilityTypes.length + 1,
      });
    }
    this.selectedFacilityType = selectedFacility?.value || 'construction_site';
    this.selectedSiteConCurrentValue = this.data?.siteConCurrent || '';
    const isExternalPowerSiteCon = !!this.data?.externalPowerSiteCon;
    this.installationTechnicalDataFG = new FormGroup({
      facilityId: new FormControl(this.data?.facilityId),
      installationId: new FormControl(this.data?.installationId),
      facilityType: new FormControl(this.selectedFacilityType, [Validators.required]),
      internalPowerSiteCon: new FormControl(this.data?.internalPowerSiteCon),
      externalPowerSiteCon: new FormControl(this.data?.externalPowerSiteCon),
      craneInPlaceCheckbox: new FormControl(this.data?.craneInPlaceCheckbox),
      siteConCurrent: new FormControl(this.data?.siteConCurrent, [Validators.required]),
      meterSwitch: new FormControl(isExternalPowerSiteCon),
      meterData: this.formBuilder.group({}),
    });
    if (this.data?.externalPowerSiteCon === 'existing_connection') {
      this.installationTechnicalDataFG.addControl('siteConPowerSubtype', new FormControl(this.data?.siteConPowerSubtype));
    }
    if (this.showMeterSwitch) {
      this.installationTechnicalDataFG.get('meterSwitch').valueChanges.subscribe(x => {
        if (x) {
          this.installationTechnicalDataFG.get('internalPowerSiteCon').setValue('');
        } else {
          this.installationTechnicalDataFG.get('externalPowerSiteCon').setValue('');
          this.onSelectExternalPowerSiteCon(null);
        }
      });
    }
  }

  prepareDropdownData(): void {
    this.facilityTypeKey = AllDropdownKeys.FacilityType.power;
    this.internalPowerSiteConTypeKey = AllDropdownKeys.InternalPowerSiteCon;
    this.externalPowerSiteConTypeKey = AllDropdownKeys.ExternalPowerSiteCon;
    this.siteConPowerSubTypeKey = AllDropdownKeys.SiteConPowerSubtype;
    this.siteConCurrentTypeKey = AllDropdownKeys.SiteConCurrent;

    this.facilityTypes = this.commonService.getDropdownData(this.facilityTypeKey);
    this.internalPowerSiteConTypes = this.commonService.getDropdownData(this.internalPowerSiteConTypeKey);
    this.externalPowerSiteConTypes = this.commonService.getDropdownData(this.externalPowerSiteConTypeKey);
    this.siteConPowerSubTypes = this.commonService.getDropdownData(this.siteConPowerSubTypeKey);
    this.siteConCurrentTypes = this.commonService.getDropdownData(this.siteConCurrentTypeKey);
  }

  onSelectExternalPowerSiteCon(event: any) {
    if (event?.value === 'existing_connection') {
      this.installationTechnicalDataFG.addControl('siteConPowerSubtype', new FormControl(this.data?.siteConPowerSubtype || ''));
    } else {
      this.installationTechnicalDataFG.removeControl('siteConPowerSubtype');
    }
    this.installationTechnicalDataFG.updateValueAndValidity();
  }

  onSelectSiteConCurrent(event: any) {
    this.selectedSiteConCurrentValue = event?.value;
  }

  saveFormData(): Promise<void> {
    const installationId = this.powerInstallationId || this.installationId;
    const requestObject: InstallationData = {
      sk_sitecon_current: this.installationTechnicalDataFG.value.siteConCurrent,
    };
    this.isLoading = true;

    return new Promise<void>((resolve, reject) => {
      this.communicationService
        .updateData(`${UrlSegments.UpdatePowerInstallation}=${installationId}`, requestObject)
        .pipe(
          tap(response => this.errorService.throwErrorIfFailure(response, true)),
          concatMap(() => this.savePowerFacilityData(installationId)),
          concatMap(() => (this.showNewMeterSection ? this.savePowerMeterData(installationId) : of(null)))
        )
        .subscribe(
          () => {
            this.isLoading = false;
            if (!this.isDetailsView) {
              this.route.navigate(['/personal-data']);
              this.stateShare.setFormState(3);
            }
            resolve();
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
            reject(error);
          }
        );
    });
  }

  private parseData() {
    const installationData: InstallationApplicationData = { ...this.commonService.installationApplicationData };
    this.externalApplicationId = this.commonService.installationApplicationId;
    this.data = {
      facilityId: installationData?.powerinstallation?.facility[0]?.facility_id || '',
      installationId: installationData?.powerinstallation?.installation_id || '',
      internalPowerSiteCon: installationData?.powerinstallation?.facility[0]?.sk_internal_power_sitecon || '',
      externalPowerSiteCon: installationData?.powerinstallation?.facility[0]?.sk_external_power_sitecon || '',
      craneInPlaceCheckbox: JSON.parse(installationData?.powerinstallation?.facility[0]?.crane_in_place ?? 'false'),
      siteConCurrent: installationData?.powerinstallation?.sk_sitecon_current || '',
      siteConPowerSubtype: installationData?.powerinstallation?.facility[0]?.sk_sitecon_power_subtype || '',
    };
    const meterData: PowerMeterData[] = installationData.powerinstallation.meter || [];
    meterData.forEach(meterItem => {
      this.meterData.push({
        meterId: meterItem.meter_id,
        installationId: meterItem.installation_id,
        meterType: meterItem.sk_power_meter_type,
        amount: meterItem.meter_count,
        selectMount: meterItem.sk_meter_mount,
        selectComm: meterItem.sk_communication,
        transformerFactor: meterItem.transducer_converterfactor,
        providedByCustomerCheckbox: meterItem.transducer_customerprovided === '1',
        customerName: meterItem.transducer_customername,
        providedByMsbCheckbox: meterItem.transducer_msbprovided === '1',
        msbName: meterItem.transducer_msbname,
        modernMeterSubtype: meterItem.sk_modern_meter_subtype,
        annualConsumption: meterItem.sk_estimated_annual_consumption,
        rlmMeasurement: meterItem.rlm_measurement,
        maxSimRefPower: meterItem?.max_sim_ref_power,
      });
    });
    if (this.isReadOnly) {
      this.installationId = installationData.powerinstallation.installation_id;
    }
    this.prepareForm();
  }

  private getApplicationData(): void {
    this.isLoading = true;

    this.facilitiesFormService.getInstallationApplication().subscribe(
      (response: GetInstallationApplicationObject) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.installationApplicationData = response.installationapplication[0];
        this.parseData();
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private handleFormData(): void {
    this.siteElectricityService.registerForComponentFormUpdate.subscribe((data: ComponentFormData) => {
      if (data.component === 'meterData') {
        this.installationTechnicalDataFG.controls.meterData = data.formData;
        this.installationTechnicalDataFG.updateValueAndValidity();
      }
    });
  }

  private saveInstallationData() {
    if (!this.powerInstallationId) {
      const jurisdictionData = this.localStorageService.getItem('location_data');
      let requestUrlSegment: string;
      const requestData: InstallationData = {
        application_id: this.applicationId,
      };
      this.isLoading = true;

      requestUrlSegment = UrlSegments.CreatePowerInstallation;
      requestData.sk_inst_change_type = jurisdictionData.powerType;
      const subscription = this.communicationService.postData(requestUrlSegment, requestData);
      subscription.subscribe(
        response => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);
          this.getApplicationData();
          this.powerInstallationId = (response as PowerInstallationResponse).powerinstallation[0].installation_id;
          this.localStorageService.setItem(`power_installation_id`, this.powerInstallationId);
        },
        error => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.getApplicationData();
    }
  }

  private savePowerFacilityData(installationId: string): Observable<unknown> {
    const formData = this.installationTechnicalDataFG.value;
    const requestObject = this.siteElectricityService.prepareFacilityBaseData(formData);
    if (!formData.facilityId) {
      requestObject.installation_id = installationId;
      return this.communicationService
        .postData(UrlSegments.CreatePowerFacility, requestObject)
        .pipe(tap((response: PowerFacilityResponse) => this.errorService.throwErrorIfFailure(response, true)));
    }

    return this.communicationService
      .updateData(`${UrlSegments.UpdatePowerFacility}=${formData.facilityId}`, requestObject)
      .pipe(tap((response: UpdatePowerFacilityResponse) => this.errorService.throwErrorIfFailure(response, true)));
  }

  private savePowerMeterData(installationId: string): Observable<unknown> {
    // MeterDataComponent emits the whole meter FormGroup ({ meters, chimneySweepConfirm }), not the meters FormArray
    const meterForm = this.installationTechnicalDataFG.value.meterData;
    const meterData = Array.isArray(meterForm) ? meterForm : meterForm?.meters ?? [];

    const requests = meterData.map(meterItem => {
      const requestObject = this.siteElectricityService.preparePowerMeterData(meterItem);
      if (!meterItem.installationId) {
        requestObject.installation_id = installationId;
        return this.communicationService
          .postData(UrlSegments.CreatePowerMeter, requestObject)
          .pipe(tap((response: PowerMeterResponse) => this.errorService.throwErrorIfFailure(response, true)));
      }
      return this.communicationService
        .updateData(`${UrlSegments.UpdatePowerMeter}=${meterItem.meterId}`, requestObject)
        .pipe(tap((response: UpdatePowerMeterResponse) => this.errorService.throwErrorIfFailure(response, true)));
    });

    return forkJoin(requests).pipe(defaultIfEmpty([]));
  }
}
